'use client';

import { useCallback, useEffect, useRef } from 'react';
import { NETWORK_SETTINGS } from '@/app/constants';
import { useFetchStream } from '@/app/hooks';
import { useStoreApi } from '@/app/providers/store';
import type { ClawV1Session } from '../types';

interface SubscribeClawSessionsStateEvent {
  session?: ClawV1Session;
}

const STATE_RECONNECT_DELAY_MS = 3000;

export const useSubscribeClawSessionsState = (clawId: string | null | undefined) => {
  const storeApi = useStoreApi();
  const sseBufferRef = useRef('');
  const clawIdRef = useRef<string>('');
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runRef = useRef<((params: { claw_id: string }) => void) | null>(null);

  const patchSession = useCallback(
    (session: ClawV1Session) => {
      if (!session.id) return;
      const { activeSessionId, sessions, patchSession: patchSessionInStore } = storeApi.getState();
      const current = sessions.find((item) => item.id === session.id);
      if (!current) return;

      const nameChanged = session.name != null && session.name !== current.name;
      const modeChanged = session.mode != null && session.mode !== current.mode;
      // run_phase 是后端权威的"本 session 还在不在跑任务"，用于在 turn.completed 丢失时
      // （典型：刷新续传）让 use-chat-stream 兜底收掉卡住的"思考中"。这条 live 通道是它的主更新源。
      const runPhaseChanged = session.run_phase != null && session.run_phase !== current.run_phase;

      const patch: Partial<ClawV1Session> = {};
      if (nameChanged) patch.name = session.name;
      if (modeChanged) patch.mode = session.mode;
      if (runPhaseChanged) patch.run_phase = session.run_phase;
      if (session.active_task_count != null && session.active_task_count !== current.active_task_count) {
        patch.active_task_count = session.active_task_count;
      }
      if (
        session.has_active_coordinator_phase != null &&
        session.has_active_coordinator_phase !== current.has_active_coordinator_phase
      ) {
        patch.has_active_coordinator_phase = session.has_active_coordinator_phase;
      }
      const nextUnreadCount = activeSessionId === session.id ? 0 : session.unread_count;
      if (nextUnreadCount != null && nextUnreadCount !== current.unread_count) {
        patch.unread_count = nextUnreadCount;
      }

      if (Object.keys(patch).length === 0) return;
      patchSessionInStore(session.id, patch);
    },
    [storeApi],
  );

  const processFrame = useCallback(
    (frame: string) => {
      const dataLines = frame
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.replace(/^data:\s*/, ''))
        .filter(Boolean);

      for (const payload of dataLines) {
        if (payload === 'heartbeat' || payload === '[DONE]') continue;
        try {
          const event = JSON.parse(payload) as SubscribeClawSessionsStateEvent;
          if (!event.session) continue;
          patchSession(event.session);
        } catch {
          // ignore malformed frames
        }
      }
    },
    [patchSession],
  );

  const clearReconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return;
    if (!clawIdRef.current) return;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      const cid = clawIdRef.current;
      if (!cid) return;
      sseBufferRef.current = '';
      runRef.current?.({ claw_id: cid });
    }, STATE_RECONNECT_DELAY_MS);
  }, []);

  const { run, close } = useFetchStream<{ claw_id: string }>('/claw/v1/claw/sessions/state/stream', {
    method: 'GET',
    baseURL: NETWORK_SETTINGS.baseUrl,
    reconnectOnClose: true,
    onMessage: (chunk) => {
      sseBufferRef.current += chunk;
      const frames = sseBufferRef.current.split('\n\n');
      sseBufferRef.current = frames.pop() ?? '';
      for (const frame of frames) {
        processFrame(frame);
      }
    },
    onClose: () => {
      if (sseBufferRef.current.trim()) {
        const tailFrame = sseBufferRef.current;
        sseBufferRef.current = '';
        processFrame(tailFrame);
      }
      scheduleReconnect();
    },
    onError: () => {
      scheduleReconnect();
    },
  });

  runRef.current = run;

  useEffect(() => {
    if (!clawId) {
      clearReconnect();
      close();
      clawIdRef.current = '';
      sseBufferRef.current = '';
      return;
    }

    clawIdRef.current = clawId;
    sseBufferRef.current = '';
    run({ claw_id: clawId });

    return () => {
      clearReconnect();
      close();
      clawIdRef.current = '';
      sseBufferRef.current = '';
    };
  }, [clawId]);
};
